import { Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import { GenericAbortSignal } from 'axios';
import { MprcButton, MprcContainer, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { AutoCompleteDataModel, AutoCompleteSuggestions } from 'components/MprcAutoComplete/AutoCompleteDataModel';
import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { GroupUsages, MemberType, TrainingShareOption, UserSuggestionsType } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupAutoComplete } from 'views/foundation/group/components/GroupAutoComplete';
import { UserAutoComplete, UserAutoCompleteQueryParams } from 'views/foundation/user/components/UserAutoComplete';

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    open?: boolean;
    trainingId: string;
    trainingName: string;
    memberType: MemberType;
    shareOption: TrainingShareOption;
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////////

export const TrainingRecommendDialog: React.FC<IProps> = (props) => {

    const { trainingId, memberType, trainingName, shareOption, open = true, onClose } = props;

    const { t } = useTranslation("lms");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [text, setText] = useState<string | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<AutoCompleteDataModel[]>([]);

    const canSave = selectedItems.length > 0;

    ///
    const handleGetDataCustom = (data: UserAutoCompleteQueryParams, signal: GenericAbortSignal | undefined) => {

        return new Promise<AutoCompleteSuggestions<AutoCompleteDataModel>>((resolve, reject) => {
            const callApi = (shareOption == TrainingShareOption.AnyUser)
                ? api.v1.suggestions.users({
                    searchText: data.searchText ?? null,
                    type: UserSuggestionsType.Any,
                    selectionHistoryType: null,
                    includeYourself: false,
                    pageNumber: data.pageNumber,
                    pageSize: data.pageSize,
                }, { signal })
                : api.v1.lmsSuggestions.audienceUserSuggestions({
                    trainingId: trainingId,
                    searchText: data.searchText ?? null,
                    includeYourself: false,
                    pageNumber: data.pageNumber,
                    pageSize: data.pageSize,
                }, { signal });

            callApi.then(result => resolve(result.data)).catch(e => reject(e));
        });
    };

    ///
    const handleSave = () => {

        if (!canSave) return;

        setSaving(true);
        api.v1.lmsStudent.addRecommendation({
            trainingId,
            memberIds: selectedItems.map(x => x.key),
            memberType,
            comments: text
        })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Training.Recommand.SavedSuccessMessage"), { variant: "success" });
                    onClose();
                } else {
                    enqueueSnackbar(t("Training.Recommand.SavedFailMessage"), { variant: "error" });
                }
            })
            .catch(e => {
                const r = MprcResolveRequestErrors(e);
                if (r.fieldsWithError != undefined && (r.fieldsWithError["trainingId"] || r.fieldsWithError["memberIds"])) {
                    const errors = (r.fieldsWithError["trainingId"] ?? []).concat(r.fieldsWithError["memberIds"] ?? []);
                    errors.forEach(item => enqueueSnackbar(item, { variant: "error" }));
                }
                else {
                    setViewError(e);
                }
            }).finally(() => {
                setSaving(false);
            });
    };

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            maxWidth="xs"
            fullWidth
        >
            <MprcDialogTitleClose
                title={t("Training.Recommand.Title")}
                subtitle={trainingName}
                onClose={() => onClose()}
                shrinkPadding
            />
            <DialogContent>
                <MprcRequestIndicator type="post" visible={saving} />
                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
                <MprcContainer>
                    {
                        memberType == MemberType.User
                            ? <UserAutoComplete
                                autoFocus
                                selectedItems={selectedItems}
                                onChange={items => setSelectedItems(items)}
                                multiSelect={true}
                                placeholder={t("Training.Recommand.UsersPlaceholder")}
                                label={t("Training.Recommand.Label")}
                                required
                                onGetDataCustom={handleGetDataCustom}
                            />
                            : <GroupAutoComplete
                                autoFocus
                                selectedItems={selectedItems}
                                groupUsages={GroupUsages.ContentSharing}
                                onChange={items => setSelectedItems(items)}
                                multiSelect={true}
                                placeholder={t("Training.Recommand.GroupsPlaceholder")}
                                label={t("Training.Recommand.Label")}
                                required
                            />
                    }
                </MprcContainer>
                <TextField
                    value={text}
                    placeholder={t("Training.Recommand.CommentPlaceholder")}
                    multiline={true}
                    rows={10}
                    onChange={(e) => setText(e.target.value)}
                    fullWidth
                    variant="outlined"
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", alignItems: "center" }}>
                <MprcButton
                    text={t("Training.Recommand.SaveButtonText")}
                    variant="contained"
                    color="primary"
                    disabled={!canSave}
                    onClick={handleSave}
                />
            </DialogActions>
        </Dialog>
    );
};
